import React from 'react'

import PropTypes from 'prop-types'
import {
  Box,
  Text,
  FormControl,
  FormHelperText,
  FormLabel,
  Button,
} from '@chakra-ui/react'
import {useFormik} from 'formik'
import * as Yup from 'yup'

import {Alert, ThemeProvider, Input, LogoHorizontal} from '../../components'

const ResendUnlockForm = ({
  authenticityToken,
  errorMessage,
  resource,
  resourceError,
  resourceName,
  unlockPath,
}) => {
  const {values, dirty, touched, errors, handleChange, handleBlur} = useFormik({
    initialValues: {
      [resourceName]: {
        email: (resource && resource.email) || '',
      },
    },
    validationSchema: Yup.object().shape({
      [resourceName]: Yup.object().shape({
        email: Yup.string()
          .email('Please enter a valid email address')
          .required('Required'),
      }),
    }),
  })

  return (
    <form method="POST" acceptCharset="UTF-8" action={unlockPath}>
      <Text textStyle="body-copy" mb="sm">
        Resend unlock instructions
      </Text>

      <input
        type="hidden"
        name="authenticity_token"
        value={authenticityToken}
      />

      <input type="hidden" name="commit" value="Resend unlock instructions" />

      {(errorMessage || !!Object.keys(resourceError).length) && (
        <Alert
          status="error"
          mb={8}
          message={
            <Box display="flex" flexDirection="column">
              {errorMessage && (
                <Text textStyle="body-small">{errorMessage}</Text>
              )}
              {Object.keys(resourceError).map((key) => (
                <Text textStyle="body-small" key={key}>
                  {`${(key.slice(0, 1).toUpperCase() + key.slice(1)).replace(
                    /_/g,
                    ' ',
                  )} ${resourceError[key].toLowerCase()}`}
                </Text>
              ))}
            </Box>
          }
        />
      )}

      <FormControl mb={4}>
        <FormLabel htmlFor="email" hidden>
          Email Address
        </FormLabel>
        <Input
          type="email"
          name={`${resourceName}[email]`}
          id={`${resourceName}_email`}
          placeholder="Email"
          aria-describedby="email-helper-text"
          onChange={handleChange}
          onBlur={handleBlur}
          errorMessage={
            (touched[resourceName] &&
              touched[resourceName].email &&
              errors[resourceName] &&
              errors[resourceName].email) ||
            ''
          }
          value={values[resourceName].email}
        />
        <FormHelperText hidden id="email-helper-text">
          We&apos;ll never share your email.
        </FormHelperText>
      </FormControl>

      <Button
        variant="secondary"
        type="submit"
        w="100%"
        isDisabled={!dirty || Object.keys(errors).length}
      >
        Resend unlock instructions
      </Button>
    </form>
  )
}

ResendUnlockForm.propTypes = {
  authenticityToken: PropTypes.string.isRequired,
  errorMessage: PropTypes.string,
  resource: PropTypes.shape({
    email: PropTypes.string,
  }),
  resourceError: PropTypes.shape({
    email: PropTypes.string,
  }),
  unlockPath: PropTypes.string.isRequired,
  resourceName: PropTypes.string.isRequired,
}

ResendUnlockForm.defaultProps = {
  errorMessage: '',
  resource: {
    email: '',
  },
  resourceError: {
    email: '',
  },
}

const ResendUnlockScreen = ({
  authenticityToken,
  errorMessage,
  resource,
  resourceError,
  resourceName,
  unlockPath,
  signInPath,
}) => {
  return (
    <ThemeProvider>
      <Box minH="100vh">
        <Box
          maxWidth="500px"
          position="absolute"
          top="40%"
          left="0"
          right="0"
          transform="translateY(-40%)"
          px="sm"
          margin="0 auto"
        >
          <LogoHorizontal w="323px" h="38px" mb="md" />
          <ResendUnlockForm
            authenticityToken={authenticityToken}
            errorMessage={errorMessage}
            resource={resource}
            resourceError={resourceError}
            resourceName={resourceName}
            unlockPath={unlockPath}
          />
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            mt={4}
          >
            <Text as="a" href={signInPath} color="secondary.blue">
              Sign in
            </Text>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  )
}

ResendUnlockScreen.propTypes = {
  resource: PropTypes.shape({
    email: PropTypes.string,
  }).isRequired,
  authenticityToken: PropTypes.string.isRequired,
  errorMessage: PropTypes.string.isRequired,
  resourceError: PropTypes.shape({
    email: PropTypes.string,
  }).isRequired,
  resourceName: PropTypes.string.isRequired,
  unlockPath: PropTypes.string.isRequired,
  signInPath: PropTypes.string.isRequired,
}

export default ResendUnlockScreen
